import React from "react";
import { colors } from "../../constants/theme";
import type { Item } from "../../types";
import { ItemPreview } from "./ItemPreview";
import { MetadataEditor } from "./MetadataEditor";
import { AgentActivityTable } from "./AgentActivityTable";
import { StatusDropdown, type StatusOption } from "../ui/StatusDropdown";

interface DetailsPaneProps {
  item: Item | null;
  detailsTab: "meta" | "agents";
  onDetailsTabChange: (tab: "meta" | "agents") => void;
  onMetaChange: (field: keyof NonNullable<Item["meta"]>, value: string) => void;
  onSave?: () => void;
  onStatusChange?: (status: StatusOption) => void;
  isSaving?: boolean;
  isUpdatingStatus?: boolean;
}

export const DetailsPane: React.FC<DetailsPaneProps> = ({ 
  item, 
  detailsTab, 
  onMetaChange,
  onSave,
  onStatusChange,
  isSaving = false,
  isUpdatingStatus = false,
}) => {
  if (!item) {
    return <span style={{ color: colors.muted }}>Select an image…</span>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {/* Preview */}
        <ItemPreview item={item} />

        {detailsTab === "meta" ? (
          <MetadataEditor item={item} onMetaChange={onMetaChange} />
        ) : (
          <AgentActivityTable activities={item.activities || []} />
        )}
      </div>

      {/* Bottom bar with Save button (left) and Status dropdown (right) */}
      {detailsTab === "meta" && (onSave || onStatusChange) && (
        <div style={{ 
          paddingTop: 12, 
          borderTop: `1px solid ${colors.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}>
          {/* Save button on the left */}
          {onSave && (
            <button
              onClick={onSave}
              disabled={isSaving}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: `1px solid ${colors.accent}`,
                background: colors.accent,
                color: "#fff",
                fontWeight: 600,
                cursor: isSaving ? "not-allowed" : "pointer",
                opacity: isSaving ? 0.6 : 1,
              }}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          )}
          
          {/* Status dropdown on the right */}
          {onStatusChange && (
            <StatusDropdown
              currentStatus={item.status}
              onStatusChange={onStatusChange}
              disabled={isUpdatingStatus}
            />
          )}
        </div>
      )}
    </div>
  );
};

